import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clearPaymentFlags } from '../utils/authFlow';
import { isExpiredAccessPlan } from '../utils/subscriptionAccess';

const FREE_ACCESSIBLE_ROUTES = ['/'];

const FullScreenSpinner = ({ message, error = '' }) => (
  <div className="min-h-screen flex items-center justify-center bg-white text-slate-600 flex-col">
    <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-[#53E0BC] mb-4"></div>
    <p className="text-center max-w-md px-4">{message}</p>
    {error ? <p className="text-center max-w-md px-4 mt-2 text-red-500">{error}</p> : null}
  </div>
);

const ProtectedRoute = ({ children }) => {
  const {
    user,
    profile,
    loading,
    accessState,
    profileStatus,
    subscriptionStatus,
    refreshProfileCache,
    fetchUserSubscription,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const handledPaymentRefreshRef = useRef(false);
  const redirectingRef = useRef(null);

  const currentPath = location.pathname;
  const isOnboardingFlashcardDeckPreview = currentPath.startsWith('/flashcards/')
    && new URLSearchParams(location.search).get('onboarding') === 'true';
  const isAccessibleRouteForFreeUser = FREE_ACCESSIBLE_ROUTES.includes(currentPath);
  const hasExpiredExamPass = isExpiredAccessPlan(profile);
  const isPaymentReturn = Boolean(
    window.location.search.includes('session_id') ||
    localStorage.getItem('paymentCompleted') ||
    sessionStorage.getItem('payment-success'),
  );

  useEffect(() => {
    if (!user?.id || !isPaymentReturn || handledPaymentRefreshRef.current) return;

    handledPaymentRefreshRef.current = true;
    void refreshProfileCache(user.id);
    void fetchUserSubscription(user.id);
  }, [fetchUserSubscription, isPaymentReturn, refreshProfileCache, user?.id]);

  useEffect(() => {
    if (!accessState?.hasPaidAccess) return;
    if (!localStorage.getItem('paymentCompleted') && !sessionStorage.getItem('payment-success')) return;

    handledPaymentRefreshRef.current = true;
    clearPaymentFlags();
  }, [accessState?.hasPaidAccess]);

  const isVerifyingAccess = Boolean(
    loading ||
    (
      user &&
      (
        accessState?.status === 'loading' ||
        accessState?.status === 'verifying'
      )
    ),
  );

  const hasProfileVerificationFailure = Boolean(
    user &&
    !loading &&
    !accessState?.hasPaidAccess &&
    (
      accessState?.status === 'needs_profile' ||
      (
        accessState?.status === 'verifying' &&
        profileStatus === 'error' &&
        subscriptionStatus !== 'loading'
      )
    ),
  );

  const returnTo = `${location.pathname}${location.search || ''}`;
  let redirectTo = null;
  let redirectState;

  if (!loading && !user) {
    redirectTo = `/signin?redirect=${encodeURIComponent(returnTo)}`;
  } else if (
    user &&
    accessState?.status === 'needs_onboarding' &&
    currentPath !== '/onboarding' &&
    !isOnboardingFlashcardDeckPreview
  ) {
    redirectTo = '/onboarding';
    redirectState = { from: location };
  } else if (user && accessState?.status === 'unpaid' && !isAccessibleRouteForFreeUser) {
    redirectTo = profile?.onboarding_completed ? '/membership' : '/onboarding';
    redirectState = redirectTo === '/membership'
      ? {
        from: location,
        message: hasExpiredExamPass
          ? 'Your exam pass has expired. Please choose a plan to continue.'
          : 'Please choose a plan to access this feature.',
      }
      : { from: location };
  }

  const redirectTargetPath = redirectTo?.split(/[?#]/)[0] || null;
  const shouldRedirect = Boolean(redirectTo && currentPath !== redirectTargetPath);

  useEffect(() => {
    if (!shouldRedirect || !redirectTo) {
      redirectingRef.current = null;
      return;
    }

    // React Strict Mode and auth hydration can mount this guard more than once.
    // Only issue one history replacement for a given target.
    if (redirectingRef.current === redirectTo) return;
    redirectingRef.current = redirectTo;

    if (redirectTo === '/membership' || redirectTo === '/onboarding') {
      localStorage.setItem('intendedRoute', currentPath);
    }

    navigate(
      redirectTo,
      redirectState ? { replace: true, state: redirectState } : { replace: true },
    );
  }, [currentPath, navigate, redirectTo, shouldRedirect]);

  if (hasProfileVerificationFailure) {
    return (
      <FullScreenSpinner
        message="We could not finish verifying your account."
        error="Please refresh and try again."
      />
    );
  }

  if (isVerifyingAccess) {
    return <FullScreenSpinner message="Verifying account access..." />;
  }

  if (shouldRedirect) {
    return <FullScreenSpinner message="Finishing your account handoff..." />;
  }

  if (!user) return <FullScreenSpinner message="Finishing your account handoff..." />;

  if (accessState?.status === 'needs_onboarding' && isOnboardingFlashcardDeckPreview) {
    return <div className="min-h-screen">{children}</div>;
  }

  if (accessState?.hasPaidAccess) {
    return <div className="min-h-screen">{children}</div>;
  }

  if (accessState?.status === 'unpaid') {
    if (isAccessibleRouteForFreeUser) {
      return <div className="min-h-screen">{children}</div>;
    }

    if (!profile?.onboarding_completed) {
      return <FullScreenSpinner message="Finishing your account handoff..." />;
    }

    return <FullScreenSpinner message="Finishing your account handoff..." />;
  }

  return <FullScreenSpinner message="Finishing your account handoff..." />;
};

export default ProtectedRoute;
